import React, { useEffect, useRef, useState } from "react";
import { useLiveNotifications } from "./LiveNotifications";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as notificationActions from "src/redux/slices/notificationSlice";
import { AppNotification } from "src/types/notification.types";
import { formatDateTime } from "src/utils/date-time";

interface NotificationBellProps {
  variant?: "desktop" | "mobile";
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  variant = "desktop",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { unreadCount } = useAppSelector(
    (state) => state.notification
  );
  const notes: AppNotification[] = useLiveNotifications().slice(0, 5);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goToNotification = (note: AppNotification) => {
    if (!note.isRead) {
      dispatch(notificationActions.markNotificationReadRequest(note._id));
    }
    setOpen(false);
    navigate(note.link || "/app/settings/notifications");
  };

  const badge = unreadCount.data > 0 && (
    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
      {unreadCount.data > 9 ? "9+" : unreadCount.data}
    </span>
  );

  if (variant === "mobile") {
    return (
      <button
        className="relative p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
        aria-label="Notifications"
        onClick={() => navigate("/app/settings/notifications")}
      >
        <Bell size={18} />
        {badge}
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        className="relative p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
        aria-label="Notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <Bell size={18} />
        {badge}
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-80 bg-white border rounded-xl shadow-lg z-50 dark:bg-gray-800 dark:border-gray-700">
          <div className="px-4 py-3 border-b dark:border-gray-700">
            <p className="font-medium text-gray-800 text-sm dark:text-gray-100">
              Notifications
            </p>
          </div>
          <ul className="max-h-80 overflow-y-auto divide-y dark:divide-gray-700">
            {notes.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                Nothing here yet.
              </li>
            )}
            {notes.map((note) => (
              <li key={note._id}>
                <button
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  onClick={() => goToNotification(note)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="flex items-center gap-1.5 text-sm font-medium text-gray-800 dark:text-gray-100">
                      {!note.isRead && (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                      )}
                      {note.title}
                    </p>
                    <span className="text-xs text-gray-400 shrink-0">
                      {formatDateTime(note.createdAt, "D MMM, HH:mm")}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {note.message}
                  </p>
                </button>
              </li>
            ))}
          </ul>
          <button
            className="w-full text-center text-sm font-medium text-primary-600 py-2.5 border-t hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
            onClick={() => {
              setOpen(false);
              navigate("/app/settings/notifications");
            }}
          >
            View all
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
